/**
 * Constants and payload types shared by recorder, server and dashboard.
 * This module must stay dependency-free: the recorder bundles it.
 */

/** rrweb top-level event types (mirrors `EventType` from @rrweb/types). */
export const RrwebEventType = {
  DomContentLoaded: 0,
  Load: 1,
  FullSnapshot: 2,
  IncrementalSnapshot: 3,
  Meta: 4,
  Custom: 5,
  Plugin: 6,
} as const;

/** Tags of the rrweb "custom" events emitted by the daevio recorder. */
export const CustomTag = {
  pageview: 'daevio:pageview',
  click: 'daevio:click',
  track: 'daevio:track',
  identify: 'daevio:identify',
} as const;

export interface PageviewPayload {
  url: string;
  path: string;
  title?: string;
}

export interface ClickPayload {
  path: string;
  /** Absolute position in the document (CSS px). */
  pageX: number;
  pageY: number;
  docW: number;
  docH: number;
  viewportW: number;
  /** CSS selector of the clicked element, relative offsets inside its box (0-100). */
  selector: string;
  offsetXPct: number;
  offsetYPct: number;
}

export interface TrackPayload {
  name: string;
  props?: Record<string, unknown>;
}

export interface IdentifyPayload {
  userId: string;
  traits?: Record<string, unknown>;
}

export interface RrwebEvent {
  type: number;
  timestamp: number;
  data: unknown;
  [key: string]: unknown;
}

export interface IngestMeta {
  url: string;
  referrer?: string;
  userAgent: string;
  language?: string;
  viewport: { w: number; h: number };
  userId?: string;
  traits?: Record<string, unknown>;
}

export interface IngestPayload {
  key: string;
  sessionId: string;
  visitorId: string;
  seq: number;
  meta?: IngestMeta;
  events: RrwebEvent[];
}

export type Device = 'desktop' | 'tablet' | 'mobile';

export const DEVICES: readonly Device[] = ['desktop', 'tablet', 'mobile'];

/** Viewport widths used to render heatmaps per device class. */
export const DEVICE_WIDTH: Record<Device, number> = {
  desktop: 1280,
  tablet: 820,
  mobile: 390,
};

export const SESSION_TIMEOUT_MS = 30 * 60 * 1000;
